import React, { useState, useEffect, useCallback } from 'react';
import { CheckCircle, XCircle, Edit3, Send, Plus, RefreshCw, Settings, LogOut, Sparkles, Clock, Facebook, ChevronDown, X, Loader, ImagePlus, Wand2, Clapperboard, Trash2, Camera, Images } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  draft: 'bg-amber-100 text-amber-700',
  approved: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-red-100 text-red-700',
  posted: 'bg-sky-100 text-sky-700',
};

const TABS = ['all', 'draft', 'approved', 'posted', 'rejected'];

export default function SocialDashboard() {
  const [token, setToken] = useState(() => localStorage.getItem('social_token'));
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState('');
  const [posts, setPosts] = useState([]);
  const [stats, setStats] = useState({});
  const [tab, setTab] = useState('all');
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [editImageInstruction, setEditImageInstruction] = useState('');
  const [showCustom, setShowCustom] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [customGuidance, setCustomGuidance] = useState('');
  const [customTopic, setCustomTopic] = useState('General Health');
  const [customPublishNow, setCustomPublishNow] = useState(false);
  const [postingFreq, setPostingFreq] = useState('twice_weekly');
  const [postingHour, setPostingHour] = useState(10);
  const [config, setConfig] = useState({});
  const [fbPageId, setFbPageId] = useState('');
  const [fbToken, setFbToken] = useState('');
  const [notifEmail, setNotifEmail] = useState('drpratt@clarendon.clinic');
  const [toast, setToast] = useState(null);
  const [publishingId, setPublishingId] = useState(null);
  const [mediaBusyId, setMediaBusyId] = useState(null);
  const [removingFBId, setRemovingFBId] = useState(null);
  const [videoPost, setVideoPost] = useState(null);
  const [videoPrompt, setVideoPrompt] = useState('');
  const [videoSize, setVideoSize] = useState('1280x720');
  const [videoDuration, setVideoDuration] = useState(4);
  const [showLibrary, setShowLibrary] = useState(false);
  const [libraryPhotos, setLibraryPhotos] = useState([]);
  const [libraryPickerPost, setLibraryPickerPost] = useState(null);
  const [libraryUploading, setLibraryUploading] = useState(false);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const authHeaders = { Authorization: `Bearer ${token}` };

  const fetchPosts = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const url = tab === 'all' ? `${API}/api/social/posts` : `${API}/api/social/posts?status=${tab}`;
      const [postsRes, statsRes] = await Promise.all([
        fetch(url, { headers: authHeaders }),
        fetch(`${API}/api/social/stats`, { headers: authHeaders }),
      ]);
      if (postsRes.status === 401) { handleLogout(); return; }
      setPosts(await postsRes.json());
      setStats(await statsRes.json());
    } catch (e) { showToast('Failed to load posts', 'error'); }
    setLoading(false);
  }, [token, tab]);

  const fetchConfig = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API}/api/social/config`, { headers: authHeaders });
      const data = await res.json();
      setConfig(data);
      setNotifEmail(data.notification_email || 'drpratt@clarendon.clinic');
      setPostingFreq(data.posting_frequency || 'twice_weekly');
      setPostingHour(data.posting_hour ?? 10);
    } catch {}
  }, [token]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);
  useEffect(() => { if (token) fetchConfig(); }, [token, fetchConfig]);

  // Keep refreshing while any AI video or image is being created
  useEffect(() => {
    if (!posts.some(p => p.media_status === 'generating_video' || p.media_status === 'generating_image')) return;
    const t = setInterval(fetchPosts, 15000);
    return () => clearInterval(t);
  }, [posts, fetchPosts]);

  const handleLogin = async () => {
    setPinError('');
    try {
      const res = await fetch(`${API}/api/social/auth`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin }),
      });
      if (!res.ok) { setPinError('Incorrect PIN. Try again.'); return; }
      const { token: t } = await res.json();
      localStorage.setItem('social_token', t);
      setToken(t);
    } catch { setPinError('Connection error. Please try again.'); }
  };

  const handleLogout = () => {
    localStorage.removeItem('social_token');
    setToken(null);
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await fetch(`${API}/api/social/generate`, {
        method: 'POST', headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ count: 8 }),
      });
      if (!res.ok) throw new Error();
      showToast('Generation started! Posts will appear over the next few minutes.');
      // Poll for new posts while AI writes text and paints images
      let polls = 0;
      const timer = setInterval(() => {
        fetchPosts();
        polls += 1;
        if (polls >= 12) { clearInterval(timer); setGenerating(false); showToast('New posts are ready — check your email too!'); }
      }, 15000);
    } catch { showToast('Generation failed. Please try again.', 'error'); setGenerating(false); }
  };

  const handleApprove = async (id) => {
    await fetch(`${API}/api/social/posts/${id}/approve`, { method: 'PUT', headers: authHeaders });
    setPosts(p => p.map(x => x.id === id ? { ...x, status: 'approved' } : x));
    showToast('Post approved!');
  };

  const handleReject = async (id) => {
    await fetch(`${API}/api/social/posts/${id}/reject`, { method: 'PUT', headers: authHeaders });
    setPosts(p => p.map(x => x.id === id ? { ...x, status: 'rejected' } : x));
    showToast('Post rejected');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this post?')) return;
    await fetch(`${API}/api/social/posts/${id}`, { method: 'DELETE', headers: authHeaders });
    setPosts(p => p.filter(x => x.id !== id));
    showToast('Post deleted');
  };

  const handlePublish = async (id) => {
    setPublishingId(id);
    try {
      const res = await fetch(`${API}/api/social/posts/${id}/publish`, { method: 'POST', headers: authHeaders });
      if (!res.ok) {
        const err = await res.json();
        showToast(err.detail || 'Failed to post to Facebook', 'error');
      } else {
        setPosts(p => p.map(x => x.id === id ? { ...x, status: 'posted' } : x));
        showToast('Posted to Facebook!');
      }
    } catch { showToast('Failed to post', 'error'); }
    setPublishingId(null);
  };

  const handleSaveEdit = async () => {
    if (!editingPost) return;
    await fetch(`${API}/api/social/posts/${editingPost.id}`, {
      method: 'PUT', headers: { ...authHeaders, 'Content-Type': 'application/json' },
      body: JSON.stringify({ caption: editingPost.caption, title: editingPost.title }),
    });
    setPosts(p => p.map(x => x.id === editingPost.id ? editingPost : x));
    setEditingPost(null);
    showToast('Post updated!');
  };

  const handleCustomPost = async () => {
    if (!customGuidance.trim()) { showToast('Please describe what you want to post', 'error'); return; }
    setGenerating(true);
    try {
      const res = await fetch(`${API}/api/social/custom`, {
        method: 'POST', headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ guidance: customGuidance, topic: customTopic, publish_now: customPublishNow }),
      });
      if (!res.ok) throw new Error();
      const post = await res.json();
      setShowCustom(false);
      setCustomGuidance('');
      setCustomPublishNow(false);
      if (post.status === 'posted') {
        showToast('Posted to Facebook!');
      } else if (post.publish_error) {
        showToast(`Post created, but not published: ${post.publish_error}`, 'error');
      } else {
        showToast('Custom post created!');
      }
      fetchPosts();
    } catch { showToast('Failed to create custom post', 'error'); }
    setGenerating(false);
  };

  const handleUploadImage = async (postId, file) => {
    setMediaBusyId(postId);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await fetch(`${API}/api/social/posts/${postId}/upload-image`, {
        method: 'POST', headers: authHeaders, body: form,
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Upload failed'); }
      showToast('Photo uploaded!');
      fetchPosts();
    } catch (e) { showToast(e.message || 'Upload failed', 'error'); }
    setMediaBusyId(null);
  };

  const handleRegenerateImage = async (postId) => {
    try {
      const res = await fetch(`${API}/api/social/posts/${postId}/regenerate-image`, {
        method: 'POST', headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error();
      showToast('Creating a new picture — the card will update on its own.');
      fetchPosts();
    } catch { showToast('Image generation failed. Try again.', 'error'); }
  };

  const handleEditImage = async () => {
    if (!editingPost || !editImageInstruction.trim()) { showToast('Describe what you want changed', 'error'); return; }
    try {
      const res = await fetch(`${API}/api/social/posts/${editingPost.id}/edit-image`, {
        method: 'POST', headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ instruction: editImageInstruction.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { showToast(data.detail || 'Could not start image edit', 'error'); return; }
      showToast('Updating the picture with your instructions — give it a minute.');
      setEditImageInstruction('');
      setEditingPost(null);
      fetchPosts();
    } catch { showToast('Could not start image edit', 'error'); }
  };

  const handleOpenVideo = (post) => {
    setVideoPost(post);
    setVideoPrompt(`${post.title}. ${post.caption.slice(0, 150)}`);
    setVideoSize('1280x720');
    setVideoDuration(4);
  };

  const handleGenerateVideo = async () => {
    if (!videoPost) return;
    try {
      const res = await fetch(`${API}/api/social/posts/${videoPost.id}/generate-video`, {
        method: 'POST', headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: videoPrompt, size: videoSize, duration: videoDuration }),
      });
      if (!res.ok) throw new Error();
      showToast('Video creation started! Takes 2-5 minutes — the card will update on its own.');
      setVideoPost(null);
      fetchPosts();
    } catch { showToast('Could not start video generation', 'error'); }
  };

  const fetchLibrary = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API}/api/social/library`, { headers: authHeaders });
      if (res.ok) setLibraryPhotos(await res.json());
    } catch {}
  }, [token]);

  useEffect(() => { if (showLibrary || libraryPickerPost) fetchLibrary(); }, [showLibrary, libraryPickerPost, fetchLibrary]);

  const handleLibraryUpload = async (file) => {
    setLibraryUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('note', '');
      const res = await fetch(`${API}/api/social/library`, { method: 'POST', headers: authHeaders, body: form });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Upload failed'); }
      showToast('Photo added to your library!');
      fetchLibrary();
    } catch (e) { showToast(e.message || 'Upload failed', 'error'); }
    setLibraryUploading(false);
  };

  const handleLibraryDelete = async (photoId) => {
    if (!window.confirm('Remove this photo from the library? Posts already using it are not affected.')) return;
    await fetch(`${API}/api/social/library/${photoId}`, { method: 'DELETE', headers: authHeaders });
    setLibraryPhotos(p => p.filter(x => x.id !== photoId));
    showToast('Photo removed from library');
  };

  const handlePickLibraryPhoto = async (photo) => {
    if (!libraryPickerPost) return;
    try {
      const res = await fetch(`${API}/api/social/posts/${libraryPickerPost.id}/use-library-photo`, {
        method: 'POST', headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ photo_id: photo.id }),
      });
      if (!res.ok) throw new Error();
      showToast('Library photo added to the post!');
      setLibraryPickerPost(null);
      fetchPosts();
    } catch { showToast('Could not apply photo', 'error'); }
  };

  const handleRemoveFromFB = async (id) => {    if (!window.confirm('Remove this post from your Facebook page? The card moves back to drafts so you can reuse or delete it.')) return;
    setRemovingFBId(id);
    try {
      const res = await fetch(`${API}/api/social/posts/${id}/facebook`, { method: 'DELETE', headers: authHeaders });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { showToast(data.detail || 'Could not remove from Facebook', 'error'); return; }
      showToast('Removed from Facebook. Card moved back to drafts.');
      fetchPosts();
    } catch { showToast('Could not remove from Facebook', 'error'); }
    setRemovingFBId(null);
  };

  const handleSaveSettings = async () => {
    const body = { notification_email: notifEmail, posting_frequency: postingFreq, posting_hour: Number(postingHour) };
    if (fbPageId) body.page_id = fbPageId;
    if (fbToken) body.page_token = fbToken;
    await fetch(`${API}/api/social/config`, {
      method: 'PUT', headers: { ...authHeaders, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    setShowSettings(false);
    fetchConfig();
    showToast('Settings saved!');
  };

  const totalPending = stats.draft || 0;
  const totalApproved = stats.approved || 0;
  const totalPosted = stats.posted || 0;

  // ── PIN GATE ──────────────────────────────────────────────────────────────
  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sky-50 to-white flex items-center justify-center px-4">
        <div className="bg-white rounded-3xl shadow-xl border border-sky-100 p-10 w-full max-w-sm text-center">
          <img src="/logo.png" alt="Clarendon Family Medicine" className="h-14 w-auto mx-auto mb-6 object-contain" />
          <h1 className="text-2xl font-bold text-sky-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Social Media Hub</h1>
          <p className="text-sky-500 text-sm mb-8">Enter your clinic PIN to continue</p>
          <input
            data-testid="pin-input"
            type="password"
            value={pin}
            onChange={e => setPin(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleLogin()}
            placeholder="Enter PIN"
            className="w-full border border-sky-200 rounded-2xl px-4 py-3 text-center text-2xl tracking-widest text-sky-900 focus:outline-none focus:ring-2 focus:ring-sky-400 mb-3"
          />
          {pinError && <p className="text-red-500 text-sm mb-3">{pinError}</p>}
          <button
            data-testid="pin-submit"
            onClick={handleLogin}
            className="w-full bg-sky-600 hover:bg-sky-700 text-white py-3 rounded-2xl font-semibold transition-colors"
          >
            Enter Dashboard
          </button>
          <a href="/" className="block mt-6 text-sky-400 text-sm hover:text-sky-600 transition-colors">← Back to Website</a>
        </div>
      </div>
    );
  }

  // ── DASHBOARD ─────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-sky-50">
      {/* Toast */}
      {toast && (
        <div data-testid="toast-message" className={`fixed top-4 right-4 z-50 px-5 py-3 rounded-2xl shadow-lg text-sm font-semibold transition-all ${toast.type === 'error' ? 'bg-red-500 text-white' : 'bg-emerald-500 text-white'}`}>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <header className="bg-white border-b border-sky-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/logo.png" alt="logo" className="h-10 w-auto object-contain" />
            <div>
              <h1 className="text-lg font-bold text-sky-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Social Media Hub</h1>
              <p className="text-xs text-sky-400">Clarendon Family Medicine</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="settings-btn" onClick={() => setShowSettings(true)} className="p-2 text-sky-400 hover:text-sky-700 hover:bg-sky-50 rounded-xl transition-colors">
              <Settings size={18} />
            </button>
            <button data-testid="logout-btn" onClick={handleLogout} className="flex items-center gap-2 text-sm text-sky-500 hover:text-sky-700 transition-colors">
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Pending Review', value: totalPending, color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-100' },
            { label: 'Approved', value: totalApproved, color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-100' },
            { label: 'Posted', value: totalPosted, color: 'text-sky-600', bg: 'bg-sky-50', border: 'border-sky-100' },
            { label: 'Rejected', value: stats.rejected || 0, color: 'text-red-400', bg: 'bg-red-50', border: 'border-red-100' },
          ].map(s => (
            <div key={s.label} data-testid={`stat-${s.label.toLowerCase().replace(/ /g, '-')}`} className={`${s.bg} border ${s.border} rounded-2xl p-5`}>
              <p className={`text-3xl font-bold ${s.color}`} style={{ fontFamily: 'Outfit, sans-serif' }}>{s.value}</p>
              <p className="text-sky-700 text-sm mt-1">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Action buttons */}
        <div className="flex flex-wrap gap-3 mb-6">
          <button
            data-testid="generate-btn"
            onClick={handleGenerate}
            disabled={generating}
            className="flex items-center gap-2 bg-sky-600 hover:bg-sky-700 text-white px-6 py-3 rounded-full font-semibold text-sm transition-all disabled:opacity-60 shadow-md shadow-sky-200"
          >
            {generating ? <Loader size={16} className="animate-spin" /> : <Sparkles size={16} />}
            {generating ? 'Generating Posts...' : 'Generate 8 Posts with AI'}
          </button>
          <button
            data-testid="custom-post-btn"
            onClick={() => setShowCustom(true)}
            className="flex items-center gap-2 border-2 border-sky-200 hover:border-sky-400 text-sky-700 px-6 py-3 rounded-full font-semibold text-sm transition-all hover:bg-sky-50"
          >
            <Plus size={16} /> Custom Post
          </button>
          <button
            data-testid="photo-library-btn"
            onClick={() => setShowLibrary(true)}
            className="flex items-center gap-2 border-2 border-sky-200 hover:border-sky-400 text-sky-700 px-6 py-3 rounded-full font-semibold text-sm transition-all hover:bg-sky-50"
          >
            <Images size={16} /> Photo Library
          </button>
          <button onClick={fetchPosts} className="flex items-center gap-2 text-sky-500 hover:text-sky-700 px-4 py-3 rounded-full text-sm transition-colors">
            <RefreshCw size={15} /> Refresh
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {TABS.map(t => (
            <button
              key={t}
              data-testid={`tab-${t}`}
              onClick={() => setTab(t)}
              className={`px-4 py-2 rounded-full text-sm font-medium capitalize transition-all ${tab === t ? 'bg-sky-600 text-white shadow-sm' : 'bg-white text-sky-600 border border-sky-200 hover:border-sky-400'}`}
            >
              {t === 'all' ? 'All Posts' : t}
              {t !== 'all' && stats[t] ? ` (${stats[t]})` : ''}
            </button>
          ))}
        </div>

        {/* Posts Grid */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader size={32} className="animate-spin text-sky-400" />
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-3xl border border-sky-100">
            <Sparkles size={40} className="text-sky-200 mx-auto mb-4" />
            <p className="text-sky-700 font-semibold text-lg">No posts yet</p>
            <p className="text-sky-400 text-sm mt-2">Click "Generate 8 Posts with AI" to create your first batch</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {posts.map(post => (
              <PostCard
                key={post.id}
                post={post}
                onApprove={handleApprove}
                onReject={handleReject}
                onEdit={setEditingPost}
                onDelete={handleDelete}
                onPublish={handlePublish}
                publishing={publishingId === post.id}
                onUploadImage={handleUploadImage}
                onRegenerateImage={handleRegenerateImage}
                onOpenVideo={handleOpenVideo}
                onRemoveFromFB={handleRemoveFromFB}
                removingFromFB={removingFBId === post.id}
                onOpenLibraryPicker={setLibraryPickerPost}
                mediaBusy={mediaBusyId === post.id}
              />
            ))}
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editingPost && (
        <Modal title="Edit Post" onClose={() => setEditingPost(null)}>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-semibold text-sky-800 block mb-1">Title</label>
              <input
                value={editingPost.title}
                onChange={e => setEditingPost({ ...editingPost, title: e.target.value })}
                className="w-full border border-sky-200 rounded-xl px-3 py-2 text-sky-900 focus:outline-none focus:ring-2 focus:ring-sky-400 text-sm"
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-sky-800 block mb-1">Caption</label>
              <textarea
                value={editingPost.caption}
                onChange={e => setEditingPost({ ...editingPost, caption: e.target.value })}
                rows={6}
                className="w-full border border-sky-200 rounded-xl px-3 py-2 text-sky-900 focus:outline-none focus:ring-2 focus:ring-sky-400 text-sm resize-none"
              />
              <p className="text-xs text-sky-400 mt-1">{editingPost.caption.length} characters</p>
            </div>
            {/* Change the picture with instructions */}
            {(editingPost.image_path || editingPost.image_filename) && !editingPost.video_path && (
              <div className="border-t border-sky-100 pt-4">
                <label className="text-sm font-semibold text-sky-800 block mb-1">Change the Picture</label>
                <textarea
                  data-testid="edit-image-instructions"
                  value={editImageInstruction}
                  onChange={e => setEditImageInstruction(e.target.value)}
                  placeholder="e.g. make it brighter, remove the clipboard, add autumn leaves, more blue tones..."
                  rows={2}
                  className="w-full border border-sky-200 rounded-xl px-3 py-2 text-sky-900 focus:outline-none focus:ring-2 focus:ring-sky-400 text-sm resize-none"
                />
                <button
                  data-testid="edit-image-submit"
                  onClick={handleEditImage}
                  className="mt-2 w-full flex items-center justify-center gap-2 bg-sky-100 hover:bg-sky-200 text-sky-700 py-2 rounded-xl font-semibold text-sm transition-colors"
                >
                  <Wand2 size={14} /> Update Picture with These Instructions
                </button>
                <p className="text-xs text-sky-400 mt-1">The AI edits the existing picture — takes about a minute, card updates on its own.</p>
              </div>
            )}
            <div className="flex gap-3 pt-2">
              <button onClick={handleSaveEdit} className="flex-1 bg-sky-600 text-white py-2 rounded-xl font-semibold text-sm hover:bg-sky-700 transition-colors">Save Changes</button>
              <button onClick={() => setEditingPost(null)} className="flex-1 border border-sky-200 text-sky-700 py-2 rounded-xl font-semibold text-sm hover:bg-sky-50 transition-colors">Cancel</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Custom Post Modal */}
      {showCustom && (
        <Modal title="Create Custom Post" onClose={() => setShowCustom(false)}>
          <div className="space-y-4">
            <p className="text-sm text-sky-600">Tell the AI what you'd like to announce or post about. Be specific!</p>
            <div>
              <label className="text-sm font-semibold text-sky-800 block mb-1">Your Guidance</label>
              <textarea
                data-testid="custom-guidance-input"
                value={customGuidance}
                onChange={e => setCustomGuidance(e.target.value)}
                placeholder="e.g. We now have an X-ray machine! Announce it with excitement and let patients know they can get imaging done in-office without driving far."
                rows={5}
                className="w-full border border-sky-200 rounded-xl px-3 py-2 text-sky-900 focus:outline-none focus:ring-2 focus:ring-sky-400 text-sm resize-none"
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-sky-800 block mb-1">Topic</label>
              <input
                value={customTopic}
                onChange={e => setCustomTopic(e.target.value)}
                placeholder="e.g. New Service Announcement"
                className="w-full border border-sky-200 rounded-xl px-3 py-2 text-sky-900 focus:outline-none focus:ring-2 focus:ring-sky-400 text-sm"
              />
            </div>
            <label className="flex items-center gap-3 bg-sky-50 rounded-xl px-4 py-3 cursor-pointer">
              <input
                data-testid="custom-post-now-toggle"
                type="checkbox"
                checked={customPublishNow}
                onChange={e => setCustomPublishNow(e.target.checked)}
                className="w-4 h-4 accent-sky-600"
              />
              <span className="text-sm text-sky-800 font-medium">Post to Facebook as soon as it's created <span className="text-sky-400 font-normal">(skips review)</span></span>
            </label>
            <div className="flex gap-3 pt-2">
              <button
                data-testid="custom-post-submit"
                onClick={handleCustomPost}
                disabled={generating}
                className="flex-1 bg-sky-600 text-white py-2 rounded-xl font-semibold text-sm hover:bg-sky-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {generating ? <><Loader size={14} className="animate-spin" /> Generating...</> : <><Sparkles size={14} /> Generate Post</>}
              </button>
              <button onClick={() => setShowCustom(false)} className="flex-1 border border-sky-200 text-sky-700 py-2 rounded-xl font-semibold text-sm hover:bg-sky-50 transition-colors">Cancel</button>
            </div>
          </div>
        </Modal>
      )}

      {/* AI Video Modal */}
      {videoPost && (
        <Modal title="Create AI Video" onClose={() => setVideoPost(null)}>
          <div className="space-y-4" data-testid="video-modal">
            <p className="text-sm text-sky-600">A short video clip makes your post stand out in the Facebook feed. Describe the scene — or keep our suggestion based on the post.</p>
            <div>
              <label className="text-sm font-semibold text-sky-800 block mb-1">Video Description</label>
              <textarea
                data-testid="video-prompt-input"
                value={videoPrompt}
                onChange={e => setVideoPrompt(e.target.value)}
                rows={4}
                placeholder="e.g. A warm sunny clinic waiting room with plants, gentle camera pan, cozy and welcoming"
                className="w-full border border-sky-200 rounded-xl px-3 py-2 text-sky-900 focus:outline-none focus:ring-2 focus:ring-sky-400 text-sm resize-none"
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-sky-800 block mb-1">Shape</label>
              <div className="flex gap-2">
                {[['1280x720', 'Wide'], ['720x1280', 'Vertical']].map(([val, label]) => (
                  <button
                    key={val}
                    data-testid={`video-size-${label.toLowerCase()}`}
                    onClick={() => setVideoSize(val)}
                    className={`flex-1 py-2 rounded-xl text-sm font-semibold transition-colors ${videoSize === val ? 'bg-sky-600 text-white' : 'border border-sky-200 text-sky-600 hover:border-sky-400'}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <p className="text-xs text-sky-400 mt-1">Wide fits the Facebook feed; Vertical feels like a phone video</p>
            </div>
            <div>
              <label className="text-sm font-semibold text-sky-800 block mb-1">Length</label>
              <div className="flex gap-2">
                {[4, 8, 12].map(sec => (
                  <button
                    key={sec}
                    data-testid={`video-duration-${sec}`}
                    onClick={() => setVideoDuration(sec)}
                    className={`flex-1 py-2 rounded-xl text-sm font-semibold transition-colors ${videoDuration === sec ? 'bg-sky-600 text-white' : 'border border-sky-200 text-sky-600 hover:border-sky-400'}`}
                  >
                    {sec}s
                  </button>
                ))}
              </div>
              <p className="text-xs text-sky-400 mt-1">Shorter = faster to create. Longer videos take several extra minutes.</p>
            </div>
            <div className="flex gap-3 pt-2">
              <button
                data-testid="video-generate-submit"
                onClick={handleGenerateVideo}
                className="flex-1 bg-sky-600 text-white py-2 rounded-xl font-semibold text-sm hover:bg-sky-700 transition-colors flex items-center justify-center gap-2"
              >
                <Clapperboard size={14} /> Create Video
              </button>
              <button onClick={() => setVideoPost(null)} className="flex-1 border border-sky-200 text-sky-700 py-2 rounded-xl font-semibold text-sm hover:bg-sky-50 transition-colors">Cancel</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Photo Library — manage mode */}
      {showLibrary && (
        <LibraryModal
          title="Photo Library"
          photos={libraryPhotos}
          uploading={libraryUploading}
          onUpload={handleLibraryUpload}
          onDelete={handleLibraryDelete}
          onClose={() => setShowLibrary(false)}
        />
      )}

      {/* Photo Library — picker mode (choose for a post) */}
      {libraryPickerPost && (
        <LibraryModal
          title={`Pick a Photo for "${libraryPickerPost.title}"`}
          photos={libraryPhotos}
          uploading={libraryUploading}
          onUpload={handleLibraryUpload}
          onPick={handlePickLibraryPhoto}
          onClose={() => setLibraryPickerPost(null)}
          pickerMode
        />
      )}

      {/* Settings Modal */}
      {showSettings && (
        <Modal title="Settings" onClose={() => setShowSettings(false)}>
          <div className="space-y-5">
            <div>
              <label className="text-sm font-semibold text-sky-800 block mb-1">Notification Email</label>
              <input
                value={notifEmail}
                onChange={e => setNotifEmail(e.target.value)}
                className="w-full border border-sky-200 rounded-xl px-3 py-2 text-sky-900 focus:outline-none focus:ring-2 focus:ring-sky-400 text-sm"
              />
              <p className="text-xs text-sky-400 mt-1">Receives email when new posts are ready to approve</p>
            </div>
            <div className="border-t border-sky-100 pt-4">
              <div className="flex items-center gap-2 mb-3">
                <Clock size={16} className="text-sky-600" />
                <h3 className="font-semibold text-sky-900 text-sm">Posting Schedule</h3>
              </div>
              <label className="text-sm font-semibold text-sky-800 block mb-1">How often should approved posts go out?</label>
              <select
                data-testid="schedule-frequency-select"
                value={postingFreq}
                onChange={e => setPostingFreq(e.target.value)}
                className="w-full border border-sky-200 rounded-xl px-3 py-2 text-sky-900 focus:outline-none focus:ring-2 focus:ring-sky-400 text-sm mb-2 bg-white"
              >
                <option value="daily">Every day</option>
                <option value="weekdays">Every weekday (Mon–Fri)</option>
                <option value="twice_weekly">Twice a week (Tue &amp; Fri)</option>
                <option value="weekly">Once a week (Mondays)</option>
                <option value="off">Off — I'll post manually</option>
              </select>
              {postingFreq !== 'off' && (
                <>
                  <label className="text-sm font-semibold text-sky-800 block mb-1">What time? (Central)</label>
                  <select
                    data-testid="schedule-hour-select"
                    value={postingHour}
                    onChange={e => setPostingHour(e.target.value)}
                    className="w-full border border-sky-200 rounded-xl px-3 py-2 text-sky-900 focus:outline-none focus:ring-2 focus:ring-sky-400 text-sm bg-white"
                  >
                    <option value={8}>8:00 AM</option>
                    <option value={10}>10:00 AM</option>
                    <option value={12}>12:00 PM</option>
                    <option value={14}>2:00 PM</option>
                    <option value={16}>4:00 PM</option>
                  </select>
                </>
              )}
              <p className="text-xs text-sky-400 mt-2">The oldest approved post goes out at each scheduled time. Use "Post Now" on any card to skip the schedule.</p>
            </div>
            <div className="border-t border-sky-100 pt-4">
              <div className="flex items-center gap-2 mb-3">
                <Facebook size={16} className="text-sky-600" />
                <h3 className="font-semibold text-sky-900 text-sm">Facebook Page Connection</h3>
                {config.has_facebook && <span className="bg-emerald-100 text-emerald-700 text-xs px-2 py-0.5 rounded-full">Connected</span>}
              </div>
              <div className="bg-sky-50 rounded-xl p-4 mb-3 text-xs text-sky-700 leading-relaxed">
                <strong>How to get your Page Access Token:</strong><br />
                1. Go to <strong>developers.facebook.com</strong> → Graph API Explorer<br />
                2. Select your app and generate a User Token with <strong>pages_manage_posts</strong> permission<br />
                3. Call <code>/me/accounts</code> to get your Page ID and Page Access Token
              </div>
              <input
                value={fbPageId}
                onChange={e => setFbPageId(e.target.value)}
                placeholder="Facebook Page ID"
                className="w-full border border-sky-200 rounded-xl px-3 py-2 text-sky-900 focus:outline-none focus:ring-2 focus:ring-sky-400 text-sm mb-2"
              />
              <input
                value={fbToken}
                onChange={e => setFbToken(e.target.value)}
                placeholder="Page Access Token (paste to update)"
                type="password"
                className="w-full border border-sky-200 rounded-xl px-3 py-2 text-sky-900 focus:outline-none focus:ring-2 focus:ring-sky-400 text-sm"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={handleSaveSettings} className="flex-1 bg-sky-600 text-white py-2 rounded-xl font-semibold text-sm hover:bg-sky-700 transition-colors">Save Settings</button>
              <button onClick={() => setShowSettings(false)} className="flex-1 border border-sky-200 text-sky-700 py-2 rounded-xl font-semibold text-sm hover:bg-sky-50 transition-colors">Cancel</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function postMedia(post) {
  if (post.video_path) return { url: `${API}/api/social/media/${post.video_path}`, isVideo: true };
  if (post.image_path) return { url: `${API}/api/social/media/${post.image_path}`, isVideo: false };
  if (post.image_filename) return { url: `${window.location.origin}/social-images/${post.image_filename}`, isVideo: false };
  return { url: null, isVideo: false };
}

function PostCard({ post, onApprove, onReject, onEdit, onDelete, onPublish, publishing, onUploadImage, onRegenerateImage, onOpenVideo, onRemoveFromFB, removingFromFB, mediaBusy, onOpenLibraryPicker }) {
  const { url: mediaUrl, isVideo } = postMedia(post);
  const generatingMedia = post.media_status === 'generating_video' || post.media_status === 'generating_image';
  const generatingVideo = post.media_status === 'generating_video';
  const fileInputRef = React.useRef(null);
  const cameraInputRef = React.useRef(null);
  return (
    <div data-testid={`post-card-${post.status}`} className="bg-white rounded-2xl border border-sky-100 overflow-hidden shadow-sm hover:shadow-md hover:shadow-sky-100 transition-all duration-300 flex flex-col">
      {/* Media */}
      <div className="bg-sky-100 aspect-square relative">
        {generatingMedia ? (
          <div data-testid="media-generating-overlay" className="w-full h-full flex flex-col items-center justify-center text-sky-500 gap-3 p-4 text-center">
            <Loader size={36} className="animate-spin" />
            <p className="text-sm font-semibold">{generatingVideo ? 'Creating your AI video...' : 'Updating your picture...'}</p>
            <p className="text-xs text-sky-400">{generatingVideo ? 'Takes 2–5 minutes.' : 'Takes about a minute.'} This card will update on its own.</p>
          </div>
        ) : mediaUrl && isVideo ? (
          <video data-testid="post-video-player" src={mediaUrl} controls className="w-full h-full object-cover" />
        ) : mediaUrl ? (
          <img src={mediaUrl} alt={post.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-sky-300">
            <Sparkles size={40} />
          </div>
        )}
        <span className={`absolute top-2 right-2 text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS_COLORS[post.status] || 'bg-gray-100 text-gray-600'}`}>
          {post.status}
        </span>
        {isVideo && !generatingVideo && (
          <span className="absolute top-2 left-2 text-xs font-semibold px-2.5 py-1 rounded-full bg-sky-900/80 text-white flex items-center gap-1">
            <Clapperboard size={11} /> Video
          </span>
        )}
      </div>
      {/* Content */}
      <div className="p-4 flex flex-col flex-1">
        <p className="text-xs font-semibold text-sky-500 uppercase tracking-wide mb-1">{post.topic}</p>
        <h3 className="text-sm font-bold text-sky-900 mb-2 leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>{post.title}</h3>
        <p className="text-xs text-sky-700 leading-relaxed line-clamp-3 flex-1">{post.caption}</p>
        {post.posted_at && (
          <p className="text-xs text-sky-400 mt-2 flex items-center gap-1"><Clock size={11} /> Posted {new Date(post.posted_at).toLocaleDateString()}</p>
        )}
        {(post.media_status === 'video_failed' || post.media_status === 'image_failed') && (
          <p data-testid="media-failed-note" className="text-xs text-red-500 mt-2 font-semibold">
            {post.media_status === 'video_failed' ? 'Video creation failed — try again.' : 'Picture update failed — try again.'}
          </p>
        )}
        {/* Actions */}
        <div className="flex flex-wrap gap-1.5 mt-3 pt-3 border-t border-sky-100">
          {post.status === 'draft' && (
            <>
              <button data-testid="approve-btn" onClick={() => onApprove(post.id)} className="flex items-center gap-1 bg-emerald-100 hover:bg-emerald-200 text-emerald-700 text-xs px-2.5 py-1.5 rounded-lg font-semibold transition-colors">
                <CheckCircle size={12} /> Approve
              </button>
              <button
                data-testid="post-now-btn"
                onClick={() => onPublish(post.id)}
                disabled={publishing}
                className="flex items-center gap-1 bg-sky-600 hover:bg-sky-700 text-white text-xs px-2.5 py-1.5 rounded-lg font-semibold transition-colors disabled:opacity-60"
              >
                {publishing ? <Loader size={11} className="animate-spin" /> : <Send size={11} />}
                Post Now
              </button>
              <button onClick={() => onReject(post.id)} className="flex items-center gap-1 bg-red-100 hover:bg-red-200 text-red-600 text-xs px-2.5 py-1.5 rounded-lg font-semibold transition-colors">
                <XCircle size={12} /> Reject
              </button>
            </>
          )}
          {post.status === 'approved' && (
            <button
              data-testid="publish-btn"
              onClick={() => onPublish(post.id)}
              disabled={publishing}
              className="flex items-center gap-1 bg-sky-600 hover:bg-sky-700 text-white text-xs px-2.5 py-1.5 rounded-lg font-semibold transition-colors disabled:opacity-60"
            >
              {publishing ? <Loader size={11} className="animate-spin" /> : <Send size={11} />}
              Post to FB
            </button>
          )}
          {post.status !== 'posted' && (
            <button onClick={() => onEdit(post)} className="flex items-center gap-1 border border-sky-200 hover:border-sky-400 text-sky-600 text-xs px-2.5 py-1.5 rounded-lg font-semibold transition-colors">
              <Edit3 size={11} /> Edit
            </button>
          )}
          {post.status === 'posted' && post.facebook_post_id && (
            <button
              data-testid="remove-from-fb-btn"
              onClick={() => onRemoveFromFB(post.id)}
              disabled={removingFromFB}
              className="flex items-center gap-1 bg-red-100 hover:bg-red-200 text-red-600 text-xs px-2.5 py-1.5 rounded-lg font-semibold transition-colors disabled:opacity-60"
            >
              {removingFromFB ? <Loader size={11} className="animate-spin" /> : <Trash2 size={11} />}
              Remove from FB
            </button>
          )}
          {post.status === 'rejected' && (
            <>
              <button data-testid="restore-btn" onClick={() => onApprove(post.id)} className="flex items-center gap-1 bg-emerald-100 hover:bg-emerald-200 text-emerald-700 text-xs px-2.5 py-1.5 rounded-lg font-semibold transition-colors">
                <CheckCircle size={11} /> Restore
              </button>
              <button
                data-testid="post-now-btn"
                onClick={() => onPublish(post.id)}
                disabled={publishing}
                className="flex items-center gap-1 bg-sky-600 hover:bg-sky-700 text-white text-xs px-2.5 py-1.5 rounded-lg font-semibold transition-colors disabled:opacity-60"
              >
                {publishing ? <Loader size={11} className="animate-spin" /> : <Send size={11} />}
                Post Now
              </button>
              <button onClick={() => onDelete(post.id)} className="flex items-center gap-1 border border-red-200 hover:bg-red-50 text-red-400 text-xs px-2.5 py-1.5 rounded-lg font-semibold transition-colors">
                <X size={11} /> Delete
              </button>
            </>
          )}
        </div>
        {/* Media tools */}
        {post.status !== 'posted' && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              data-testid="upload-image-input"
              onChange={e => { if (e.target.files?.[0]) onUploadImage(post.id, e.target.files[0]); e.target.value = ''; }}
            />
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              data-testid="take-photo-input"
              onChange={e => { if (e.target.files?.[0]) onUploadImage(post.id, e.target.files[0]); e.target.value = ''; }}
            />
            <button
              data-testid="take-photo-btn"
              onClick={() => cameraInputRef.current?.click()}
              disabled={mediaBusy || generatingMedia}
              className="flex items-center gap-1 bg-sky-100 hover:bg-sky-200 text-sky-700 text-xs px-2.5 py-1.5 rounded-lg font-semibold transition-colors disabled:opacity-50"
            >
              <Camera size={12} /> Take Photo
            </button>
            <button
              data-testid="library-picker-btn"
              onClick={() => onOpenLibraryPicker(post)}
              disabled={mediaBusy || generatingMedia}
              className="flex items-center gap-1 bg-sky-100 hover:bg-sky-200 text-sky-700 text-xs px-2.5 py-1.5 rounded-lg font-semibold transition-colors disabled:opacity-50"
            >
              <Images size={12} /> Library
            </button>
            <button
              data-testid="upload-image-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={mediaBusy || generatingMedia}
              className="flex items-center gap-1 bg-sky-100 hover:bg-sky-200 text-sky-700 text-xs px-2.5 py-1.5 rounded-lg font-semibold transition-colors disabled:opacity-50"
            >
              <ImagePlus size={12} /> Upload Photo
            </button>
            <button
              data-testid="regenerate-image-btn"
              onClick={() => onRegenerateImage(post.id)}
              disabled={mediaBusy || generatingMedia}
              className="flex items-center gap-1 bg-sky-100 hover:bg-sky-200 text-sky-700 text-xs px-2.5 py-1.5 rounded-lg font-semibold transition-colors disabled:opacity-50"
            >
              {mediaBusy ? <Loader size={12} className="animate-spin" /> : <Wand2 size={12} />} AI Image
            </button>
            <button
              data-testid="ai-video-btn"
              onClick={() => onOpenVideo(post)}
              disabled={mediaBusy || generatingMedia}
              className="flex items-center gap-1 bg-sky-600 hover:bg-sky-700 text-white text-xs px-2.5 py-1.5 rounded-lg font-semibold transition-colors disabled:opacity-50"
            >
              <Clapperboard size={12} /> AI Video
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function LibraryModal({ title, photos, uploading, onUpload, onDelete, onPick, onClose, pickerMode }) {
  const fileRef = React.useRef(null);
  const cameraRef = React.useRef(null);
  const handleFile = (e) => { if (e.target.files?.[0]) onUpload(e.target.files[0]); e.target.value = ''; };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-sky-900/30 backdrop-blur-sm p-4">
      <div data-testid="photo-library-modal" className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-sky-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</h2>
          <button onClick={onClose} className="p-1.5 text-sky-400 hover:text-sky-700 hover:bg-sky-50 rounded-lg transition-colors"><X size={18} /></button>
        </div>

        {/* Upload row */}
        <div className="flex flex-wrap gap-2 mb-5">
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid="library-upload-input" onChange={handleFile} />
          <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" data-testid="library-take-photo-input" onChange={handleFile} />
          <button
            data-testid="library-take-photo-btn"
            onClick={() => cameraRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-2 bg-sky-600 hover:bg-sky-700 text-white px-4 py-2.5 rounded-full text-sm font-semibold transition-colors disabled:opacity-60"
          >
            {uploading ? <Loader size={14} className="animate-spin" /> : <Camera size={14} />} Take Photo
          </button>
          <button
            data-testid="library-upload-btn"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-2 border-2 border-sky-200 hover:border-sky-400 text-sky-700 px-4 py-2.5 rounded-full text-sm font-semibold transition-colors disabled:opacity-60"
          >
            <ImagePlus size={14} /> Upload from Device
          </button>
        </div>

        {photos.length === 0 ? (
          <div className="text-center py-10 bg-sky-50 rounded-2xl">
            <Images size={36} className="text-sky-200 mx-auto mb-3" />
            <p className="text-sky-700 font-semibold">No photos yet</p>
            <p className="text-sky-400 text-sm mt-1">Snap or upload your first clinic photo — you can reuse it on any post</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {photos.map(photo => (
              <div key={photo.id} data-testid="library-photo" className="relative group rounded-xl overflow-hidden border border-sky-100 bg-sky-50 aspect-square">
                <img src={`${API}/api/social/media/${photo.storage_path}`} alt={photo.note || 'Clinic photo'} className="w-full h-full object-cover" loading="lazy" />
                <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-sky-900/70 to-transparent p-2 flex justify-center">
                  {pickerMode ? (
                    <button
                      data-testid="library-use-photo-btn"
                      onClick={() => onPick(photo)}
                      className="bg-white text-sky-700 text-xs font-bold px-3 py-2 rounded-full shadow hover:bg-sky-50 transition-colors"
                    >
                      Use This Photo
                    </button>
                  ) : (
                    <button
                      data-testid="library-delete-btn"
                      onClick={() => onDelete(photo.id)}
                      className="bg-white text-red-500 text-xs font-bold px-3 py-2 rounded-full shadow hover:bg-red-50 transition-colors flex items-center gap-1"
                    >
                      <Trash2 size={12} /> Remove
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        {pickerMode && photos.length > 0 && (
          <p className="text-xs text-sky-400 mt-4 text-center">Hover a photo and choose "Use This Photo" — it becomes this post's picture</p>
        )}
      </div>
    </div>
  );
}

function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-sky-900/30 backdrop-blur-sm p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-xl font-bold text-sky-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</h2>
          <button onClick={onClose} className="p-1.5 text-sky-400 hover:text-sky-700 hover:bg-sky-50 rounded-lg transition-colors"><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
